// E-posta bağlantılarıyla açılan sayfalar: şifremi unuttum, yeni şifre, e-posta doğrulama.
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { LuArrowLeft, LuMailCheck } from 'react-icons/lu';
import type { MeResponse } from '../../shared/plans';
import { NovaMark } from '../components/ui';
import { api, logout } from '../lib/api';

const MIN_PASSWORD_LENGTH = 10;

function AuthCard({ title, text, children, back = true }: { title: string; text?: string; children?: ReactNode; back?: boolean }) {
  return (
    <div className="auth-screen">
      {back && (
        <a className="auth-back" href="/giris">
          <LuArrowLeft /> Girişe dön
        </a>
      )}
      <div className="card auth-card">
        <a className="brand" href="/">
          <NovaMark size={36} />
          <span className="brand-name">Nova Class</span>
        </a>
        <h1>{title}</h1>
        {text && <p className="muted small">{text}</p>}
        {children}
      </div>
    </div>
  );
}

/** Bağlantıdaki belirteci okur ve adres çubuğundan siler; geçmişte ve ekran paylaşımında görünmesin. */
function useUrlToken(): string {
  const [token] = useState(() => new URLSearchParams(location.search).get('token') ?? '');
  useEffect(() => {
    if (location.search) history.replaceState(null, '', location.pathname);
  }, []);
  return token;
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/forgot', { method: 'POST', body: { email } });
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return <AuthCard title="E-postanızı kontrol edin" text={`${email} adresi kayıtlıysa şifre sıfırlama bağlantısı gönderdik. Bağlantı 1 saat geçerlidir. Gelen kutunuzda göremezseniz istenmeyen e-postalara da bakın.`} />;
  }
  return (
    <AuthCard title="Şifrenizi mi unuttunuz?" text="E-posta adresinizi yazın, size yeni şifre belirleme bağlantısı gönderelim.">
      <form onSubmit={submit}>
        <label className="field">
          <span>E-posta</span>
          <input id="forgot-email" type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
          {busy ? 'Gönderiliyor…' : 'Bağlantı gönder'}
        </button>
      </form>
    </AuthCard>
  );
}

export function ResetPassword() {
  const token = useUrlToken();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/reset', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthCard title="Bağlantı geçersiz" text="Bu sayfa e-postadaki bağlantıyla açılmalıdır. Yeni bir bağlantı isteyebilirsiniz.">
        <a className="btn btn-primary btn-block btn-lg" href="/sifremi-unuttum">
          Yeni bağlantı iste
        </a>
      </AuthCard>
    );
  }
  if (done) {
    return (
      <AuthCard title="Şifreniz değiştirildi" text="Güvenliğiniz için tüm cihazlardaki oturumlar kapatıldı. Yeni şifrenizle giriş yapabilirsiniz." back={false}>
        <a className="btn btn-primary btn-block btn-lg" href="/giris">
          Giriş yap
        </a>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Yeni şifrenizi belirleyin">
      <form onSubmit={submit}>
        <label className="field">
          <span>Yeni şifre (en az {MIN_PASSWORD_LENGTH} karakter)</span>
          <input id="reset-password" type="password" autoComplete="new-password" autoFocus required minLength={MIN_PASSWORD_LENGTH} value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && (
          <p className="form-error">
            {error} <a href="/sifremi-unuttum">Yeni bağlantı iste</a>
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
          {busy ? 'Kaydediliyor…' : 'Şifreyi kaydet'}
        </button>
      </form>
    </AuthCard>
  );
}

export function VerifyEmail() {
  const token = useUrlToken();
  const [state, setState] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed');
  const [error, setError] = useState('Bu sayfa e-postadaki bağlantıyla açılmalıdır.');
  const started = useRef(false);

  // Sayfa açılınca doğrular; e-posta tarayıcılarının bağlantıyı önizlemesi hesabı doğrulamaz.
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api('/api/auth/verify', { method: 'POST', body: { token } }).then(
      () => setState('done'),
      (err: Error) => {
        setError(err.message);
        setState('failed');
      },
    );
  }, [token]);

  if (state === 'working') return <AuthCard title="E-postanız doğrulanıyor…" back={false} />;
  if (state === 'done') {
    return (
      <AuthCard title="E-postanız doğrulandı" text="Hesabınız hazır. Artık ilk dersinizi başlatabilirsiniz." back={false}>
        <a className="btn btn-primary btn-block btn-lg" href="/">
          Panele git
        </a>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="E-posta doğrulanamadı" text={error}>
      <a className="btn btn-primary btn-block btn-lg" href="/">
        Yeni bağlantı iste
      </a>
    </AuthCard>
  );
}

/** Doğrulanmamış hesap panele geçemez; sunucu da aynı kuralı uygular. */
export function VerifyPending({ user, onChange }: { user: NonNullable<MeResponse['user']>; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resend = async () => {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await api('/api/auth/resend-verification', { method: 'POST' });
      setNote('Yeni bağlantı gönderildi.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="E-postanızı doğrulayın" back={false}>
      <div className="verify-icon">
        <LuMailCheck />
      </div>
      <p className="muted small">
        <strong>{user.email}</strong> adresine bir doğrulama bağlantısı gönderdik. Ders başlatmak için bağlantıya tıklayın. Gelen kutunuzda göremezseniz istenmeyen e-postalara da bakın.
      </p>
      {note && <p className="form-note-ok">{note}</p>}
      {error && <p className="form-error">{error}</p>}
      <button type="button" className="btn btn-primary btn-block btn-lg" disabled={busy} onClick={() => void resend()}>
        {busy ? 'Gönderiliyor…' : 'Bağlantıyı tekrar gönder'}
      </button>
      <button type="button" className="btn btn-block auth-secondary" onClick={() => void logout().finally(onChange)}>
        Çıkış yap
      </button>
    </AuthCard>
  );
}
